/**
 * Shared page-level states (loading/error/empty) composed from design-system
 * primitives. Loading renders skeleton shapes mirroring a generic page
 * (title + prose blocks) — never a spinner — announced through a polite
 * status region; screens with a known layout render their own mirroring
 * skeletons instead. Errors are an alert Card with at most one retry action;
 * empties delegate to the EmptyState primitive.
 */

import type { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { EmptyState as UiEmptyState } from "@/components/ui/empty-state";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";

export function PageLoading({ label = "Loading…" }: { label?: string }) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      className="mx-auto w-full max-w-3xl px-4 py-6"
    >
      <span className="sr-only">{label}</span>
      {/* Mirrors the shape of a loaded page: title, then prose blocks. */}
      <div aria-hidden="true" className="flex flex-col gap-6">
        <Skeleton className="h-7 w-40" />
        <div className="space-y-3">
          <Skeleton className="h-4 w-11/12" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
        <SkeletonText lines={3} />
      </div>
    </div>
  );
}

export function ErrorState({
  title = "Something went wrong",
  error,
  onRetry,
}: {
  title?: string;
  error?: Error | string | null;
  onRetry?: () => void;
}) {
  const detail = error instanceof Error ? error.message : (error ?? undefined);
  return (
    <Card role="alert" className="m-4 max-w-3xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle
            aria-hidden="true"
            className="size-4 text-status-blocked"
          />
          {title}
        </CardTitle>
        {detail ? <CardDescription>{detail}</CardDescription> : null}
      </CardHeader>
      {onRetry ? (
        <CardContent>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </CardContent>
      ) : null}
    </Card>
  );
}

/** Delegates to the design-system EmptyState, keeping the legacy margin. */
export function EmptyState(props: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return <UiEmptyState className="m-4" {...props} />;
}
